import apiClient from './api';
import {
  Analysis,
  CreateAnalysisRequest,
  EnhancedAnalysisRequest,
  AnalysesResponse,
  AnalysisDetailsResponse,
  AnalysisQueryParams,
} from '../types/api/analyses';

export class AnalysesService {
  // Analyses endpoints
  static async getAnalyses(params?: AnalysisQueryParams): Promise<AnalysesResponse> {
    const response = await apiClient.get('/analyses/', { params });
    return response.data;
  }

  static async getAnalysis(analysisId: string): Promise<AnalysisDetailsResponse> {
    const response = await apiClient.get(`/analyses/${analysisId}/`);
    return response.data;
  }

  static async createAnalysis(data: CreateAnalysisRequest): Promise<Analysis> {
    const formData = new FormData();
    
    // Add text fields
    formData.append('test_product_name', data.test_product_name);
    if (data.test_product_seller) formData.append('test_product_seller', data.test_product_seller);
    if (data.test_product_url) formData.append('test_product_url', data.test_product_url);
    if (data.test_product_price) formData.append('test_product_price', data.test_product_price.toString());
    if (data.reference_product_id) formData.append('reference_product_id', data.reference_product_id.toString());
    formData.append('use_llm', data.use_llm.toString());
    if (data.llm_provider) formData.append('llm_provider', data.llm_provider);

    // Add test images
    data.test_images.forEach((image) => {
      formData.append('test_images', image);
    });

    // Add reference images if provided
    if (data.reference_images) {
      data.reference_images.forEach((image) => {
        formData.append('reference_images', image);
      });
    }

    const response = await apiClient.post('/analyses/', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  }

  static async createEnhancedAnalysis(data: EnhancedAnalysisRequest): Promise<Analysis> {
    const formData = new FormData();
    
    // Add all fields from basic analysis
    formData.append('test_product_name', data.test_product_name);
    if (data.test_product_seller) formData.append('test_product_seller', data.test_product_seller);
    if (data.test_product_url) formData.append('test_product_url', data.test_product_url);
    if (data.test_product_price) formData.append('test_product_price', data.test_product_price.toString());
    if (data.reference_product_id) formData.append('reference_product_id', data.reference_product_id.toString());
    formData.append('use_llm', data.use_llm.toString());
    if (data.llm_provider) formData.append('llm_provider', data.llm_provider);

    // Enhanced analysis specific fields
    formData.append('use_enhanced_analysis', data.use_enhanced_analysis.toString());
    formData.append('use_analytics', data.use_analytics.toString());
    
    if (data.product_data) {
      formData.append('product_data', JSON.stringify(data.product_data));
    }

    // Add images
    data.test_images.forEach((image) => {
      formData.append('test_images', image);
    });

    if (data.reference_images) {
      data.reference_images.forEach((image) => {
        formData.append('reference_images', image);
      });
    }

    const response = await apiClient.post('/analyses/enhanced/', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  }

  static async deleteAnalysis(analysisId: string): Promise<void> {
    await apiClient.delete(`/analyses/${analysisId}/`);
  }

  static async updateAnalysisStatus(analysisId: string, status: string): Promise<Analysis> {
    const response = await apiClient.patch(`/analyses/${analysisId}/`, { status });
    return response.data;
  }


  // Analysis statistics
  static async getAnalysisStats(params?: { period?: string }): Promise<any> {
    const response = await apiClient.get('/analyses/stats/', { params });
    return response.data;
  }

  // Export analysis results
  static async exportAnalysis(analysisId: string, format: 'pdf' | 'json' | 'csv'): Promise<Blob> {
    const response = await apiClient.get(`/analyses/${analysisId}/export/`, {
      params: { format },
      responseType: 'blob',
    });
    return response.data;
  }

  // Batch operations
  static async bulkDeleteAnalyses(analysisIds: string[]): Promise<void> {
    await apiClient.post('/analyses/bulk-delete/', { analysis_ids: analysisIds });
  }

  static async bulkUpdateStatus(analysisIds: string[], status: string): Promise<void> {
    await apiClient.post('/analyses/bulk-update-status/', { 
      analysis_ids: analysisIds, 
      status 
    });
  }
}

export default AnalysesService;